import { useCallback } from 'react';
import {
  AppBundle,
  AppBundleRoutes,
  getEntrypoint,
  getImportShim,
  registerPreloadPromise,
  shouldSkipJourneysPreload,
} from '../utils/app-bundles';
import { sessionsStore } from '@customerio/hydra-session-state';
import { AppBundleLoader } from '@/components/app-bundle-loader';
import { sentryAddAppLoadBreadcrumb } from '@/utils/sentry';
import {
  plumaColorSchemePreferenceStore,
  plumaColorSchemeStore,
  plumaContextValueStore,
  plumaThemeStore,
  plumaThemesStore,
} from '@/components/connected-pluma-provider';
import { AccountFeatureFlag, hasFeatureFlag } from '@/services/auth/feature-flags';
import { recordJourneysBoot } from '@/utils/journeys-boot-tracker';
import { recordRumMilestone, RumMilestone } from '@/services/rum';

// Types for dynamically loaded Journeys app
type JourneysAppInstance = {
  injectScripts: () => Promise<void>;
  create: () => void;
  destroy: () => void;
};

type JourneysAppConstructor = new (
  element: HTMLElement,
  options?: {
    hydraProps?: {
      scrollContainerSelector?: string;
      useNewWorkspacesUrls?: boolean;
      journeysTaxonomyRename?: boolean;
      plumaContextValue?: typeof plumaContextValueStore;
      plumaTheme?: typeof plumaThemeStore;
      plumaColorScheme?: typeof plumaColorSchemeStore;
      plumaColorSchemePreference?: typeof plumaColorSchemePreferenceStore;
      plumaThemes?: typeof plumaThemesStore;
    };
  },
) => JourneysAppInstance;

type JourneysModule = {
  App: JourneysAppConstructor;
  default?: unknown;
};

export default function Journeys() {
  return <JourneysBackgroundApp />;
}

/**
 * Single-instance boot contract
 * ------------------------------
 * Hydra boots the embedded Journeys (Ember) app **once per page load** and reuses that
 * single instance for every client-side navigation — authenticated or not. The shared
 * instance is owned by `AppBundleLoader`, which keeps it alive in a root-level context
 * (`BackgroundAppBundleContextProvider`) and hides/shows it across route changes instead
 * of destroying and rebooting it. Consequences that the rest of this file depends on:
 *
 * 1. One boot per page load. Every Journeys boot constructs a fresh Ember app (and its
 *    services, e.g. `billing`, which fetches `billing/profile` on startup), so repeated
 *    boots are expensive and observable. Reuse, don't reboot.
 * 2. Boot-time context = whatever is available at load. Anything read here at boot
 *    (currently only `journeysTaxonomyRename`) is consumed once by the embedded router
 *    and fixed for the life of the instance. When the app boots on an unauthenticated
 *    page there is no account yet; account-scoped reads must fall back to a safe default
 *    rather than assume an account is present.
 * 3. Auth/account changes are full page reloads. Login, logout, account switch, and
 *    masquerade all navigate via `window.location` (not client-side routing), so a fresh
 *    instance always boots with the correct account context. We therefore never mutate
 *    boot-time context on a live instance, and never carry one instance across the
 *    logged-out → logged-in boundary in-session. (The lone in-session, no-reload auth
 *    transition — signup → post_registration → app — stays within this one instance and
 *    is compatible with the contract.)
 *
 * (There used to be a second `JourneysNonBackgroundApp` mount that booted its own Ember
 * app and destroyed it on unmount — the source of the reboot storms this work fixes. It
 * has been removed; every Journeys page now renders this shared instance.)
 */
function JourneysBackgroundApp() {
  const renderJourneysApp = useCallback(async (appWrapper: HTMLDivElement) => {
    // Direct call: the legacy-provider mirror no-ops outside the
    // authenticated layout, and Journeys also boots on login/signup.
    recordRumMilestone(RumMilestone.ChildAppLoadStart);
    sentryAddAppLoadBreadcrumb('journeys background entrypoint requested', { appBundle: AppBundle.Journeys });
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    const entrypoint = (await getEntrypoint(AppBundle.Journeys)) as JourneysModule;
    sentryAddAppLoadBreadcrumb('journeys background entrypoint resolved', { appBundle: AppBundle.Journeys });

    // Boot-time context, read once (see the single-instance boot contract above). The
    // embedded Ember router consumes `journeysTaxonomyRename` a single time when it builds
    // its route map, so its value is fixed for the life of this instance. This instance is
    // shared across every page in a load, including unauthenticated ones (login/signup/etc.)
    // where no account exists yet: `hasFeatureFlag` returns the configured default when
    // there is no account, so booting without auth is safe. Auth/account changes are full
    // reloads, so a later authenticated boot re-reads this flag with the right account.
    // Either rename flag puts the embedded router on the renamed URL segments; the
    // "final" flag enables the rename on its own (it just drops the transitional copy).
    const journeysTaxonomyRename =
      hasFeatureFlag(AccountFeatureFlag.JourneysTaxonomyRename) ||
      hasFeatureFlag(AccountFeatureFlag.JourneysTaxonomyRenameFinal);
    const appInstance = new entrypoint.App(appWrapper, {
      hydraProps: {
        scrollContainerSelector: '#embedded-app-container',
        useNewWorkspacesUrls: true,
        journeysTaxonomyRename,
        plumaContextValue: plumaContextValueStore,
        plumaTheme: plumaThemeStore,
        plumaColorScheme: plumaColorSchemeStore,
        plumaColorSchemePreference: plumaColorSchemePreferenceStore,
        plumaThemes: plumaThemesStore,
      },
    });

    sentryAddAppLoadBreadcrumb('journeys background inject scripts started', { appBundle: AppBundle.Journeys });
    await appInstance.injectScripts();
    sentryAddAppLoadBreadcrumb('journeys background inject scripts resolved', { appBundle: AppBundle.Journeys });
    sentryAddAppLoadBreadcrumb('journeys background create called', { appBundle: AppBundle.Journeys });
    appInstance.create();
    recordJourneysBoot('background');
    // Closest shell-observable "app is up" point; in-app milestones come later.
    recordRumMilestone(RumMilestone.ChildAppRendered);

    return () => {
      appInstance.destroy();
    };
  }, []);

  performance.mark('hydra:journeys-background-app:render');

  return (
    <AppBundleLoader
      appBundle={AppBundle.Journeys}
      className="journeys-app"
      style="height: fit-content;"
      renderApp={renderJourneysApp}
    />
  );
}

export const preloadJourneysAppBundle = () => {
  // Pages that always leave via a full-document navigation discard anything preloaded here.
  // See `shouldSkipJourneysPreload`.
  if (
    shouldSkipJourneysPreload({
      pathname: window.location.pathname,
      hasPersistedSession: sessionsStore.get().length > 0,
    })
  ) {
    return;
  }

  // If the URL contains other app bundles, don't preload Journeys
  // (doing by exclusion, rather than checking for the presence of Journeys, because Journeys is the default app bundle)
  const url = String(window.location.href);
  const otherAppBundles = Object.values(AppBundleRoutes).filter(
    (route) => route !== AppBundleRoutes[AppBundle.Journeys],
  );
  if (otherAppBundles.some((bundle) => url.includes(bundle))) {
    return;
  }

  const preloadJourneysApp = async () => {
    performance.mark('hydra:journeys-preload:init');
    sentryAddAppLoadBreadcrumb('journeys preload entrypoint requested', { appBundle: AppBundle.Journeys });
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    const entrypoint = (await getImportShim()(AppBundle.Journeys)) as JourneysModule;
    sentryAddAppLoadBreadcrumb('journeys preload entrypoint resolved', { appBundle: AppBundle.Journeys });

    performance.mark('hydra:journeys-preload:importshim-entrypoint-ready');

    // The App constructor requires an element to be passed, but it's not
    // actually used if we don't call `create()`
    const dummyAppWrapper = document.createElement('div');
    const appInstance = new entrypoint.App(dummyAppWrapper);
    performance.mark('hydra:journeys-preload:inject-scripts');
    sentryAddAppLoadBreadcrumb('journeys preload inject scripts started', { appBundle: AppBundle.Journeys });
    await appInstance.injectScripts();
    sentryAddAppLoadBreadcrumb('journeys preload inject scripts resolved', { appBundle: AppBundle.Journeys });
    performance.mark('hydra:journeys-preload:inject-scripts-complete');
    dummyAppWrapper.remove();
    appInstance.destroy();
    return entrypoint;
  };

  registerPreloadPromise(AppBundle.Journeys, preloadJourneysApp);
};
